import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot, AreaPlot } from "@mui/x-charts/LineChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsLegend } from "@mui/x-charts/ChartsLegend";
import { ChartsSurface } from "@mui/x-charts/ChartsSurface";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { useCfmContext } from "../../../Context/CfmContext";
import { getAgeFromDOB } from "../../../Context/Utils/GetAgeFromDOB";
import { hexToRgba } from "./GetGradientColor";
import "../../../../../../css/CashflowModeller2/Charts/Graphs.css";
import { useRef } from "react";
import { ChartDataProviderPro } from "@mui/x-charts-pro/ChartDataProviderPro";
import CustomChartWrapper from "../Utils/CustomWrapper";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import useGraphHeight from "../Utils/useGraphHeight";

/**
 * PensionGraph component renders a combined line and bar chart visualizing pension fund values and withdrawals
 * for a selected person, using data from the Cashflow Modeller context.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.selectedPerson - The name of the person whose pension data is to be displayed.
 * @param {boolean} [props.animated=false] - Whether to animate the chart transitions.
 * @param {boolean} [props.isPrint=false] - Whether the chart is being rendered for printing (affects sizing).
 * @param {string} [props.idOverride] - Optional override for the chart container's DOM id.
 *
 * @returns {JSX.Element} The rendered pension graph chart.
 *
 * @example
 * <PensionGraph selectedPerson="John Doe" animated={true} isPrint={false} />
 */
const PensionGraph = ({
  selectedPerson,
  animated = false,
  isPrint = false,
  idOverride,
  apiRef = null, // Optional API ref for external control, e.g., printing
}) => {
  const { state, dispatch } = useCfmContext();
  const person = state.details?.find((d) => d.name?.startsWith(selectedPerson));
  const currentAge = getAgeFromDOB(person?.dob) ?? 18;
  const chartOnlyRef = useRef();
  const mountedRef = useRef(false);

  const colors = [
    {
      adhocContribution: "#FF5722",
      income: "#62B966",
      tfc: "#8DD3C7",
      phased: "#9C27B0",
      adhoc: "#E91E63",
      fund: "#57B5E7",
      regularIncome: "#6a1b9a",
    },
    {
      fund: "#ff8a65",
      income: "#81C784",
      tfc: "#c792ea",
      phased: "#27cde2",
      adhoc: "#4db6ac",
      adhocContribution: "#FF8A65",
      regularIncome: "#8e24aa",
    },
    {
      fund: "#f57c00",
      income: "#2e7d32",
      tfc: "#1565c0",
      phased: "#6a1b9a",
      adhoc: "#8e24aa",
      adhocContribution: "#fdd835",
      regularIncome: "#E91E63",
    },
    {
      fund: "#ffa000",
      income: "#81c784",
      tfc: "#5e35b1",
      phased: "#ba68c8",
      adhoc: "#e1bee7",
      adhocContribution: "#fff176",
      regularIncome: "#57B5E7",
    },
    // {
    //   fund: "#f57c00",
    //   income: "#2e7d32",
    //   tfc: "#1565c0",

    //   adhocContribution: "#fdd835",
    // },
    // {
    //   fund: "#ffa000",
    //   income: "#81c784",
    //   tfc: "#5e35b1",
    //   phased: "#ba68c8",
    //   adhoc: "#e1bee7",
    //   adhocContribution: "#fff176",
    // },
  ];

  const hasPensions = (state.pensions || []).filter(
    (entry) => entry.name === selectedPerson
  );

  const personPensions =
    hasPensions.length > 0
      ? (state.pensionResults || []).filter(
          (entry) => entry.name === selectedPerson
        )
      : [];

  const allAgesSet = new Set();
  personPensions.forEach((p) => {
    p.pensionFunds?.forEach((row) => {
      if (row.age >= currentAge) allAgesSet.add(row.age);
    });
  });
  const allAges = Array.from(allAgesSet).sort((a, b) => a - b);

  const parsePos = (v) => {
    const n = parseFloat((v || "0").replace(/[^0-9.-]+/g, ""));
    return n >= 0 ? n : null;
  };

  const labelToIndexMap = new Map();
  let colorCounter = 0;

  const placeholderSeries = [
    {
      type: "line",
      data: [0],
      label: "Fund Value",
      color: "#f57c00",
    },
    {
      type: "bar",
      stack: "pension",
      data: [0],
      label: "Income",
      color: "#2e7d32",
    },
  ];

  const series = personPensions.flatMap((pension) => {
    const label = `${pension.type || "Pension"}`;
    if (!labelToIndexMap.has(label)) {
      labelToIndexMap.set(label, colorCounter++);
    }
    const colorSet = colors[labelToIndexMap.get(label) % colors.length];

    const fundMap = new Map();
    pension.pensionFunds?.forEach((row) => {
      if (row.age >= currentAge) {
        fundMap.set(row.age, {
          fund: parsePos(row.fundValue),
          income: parsePos(row.taxedDrawdown),
          tfc: parsePos(row.tflsWithdrawal),
          phased: parsePos(row.phasedTfc),
          adhoc: parsePos(row.adhocWithdrawal),
          adhocContribution: parsePos(row.adhocContribution),
          regularIncome: parsePos(row.contribution),
        });
      }
    });

    return [
      {
        type: "line",
        area: true,
        data: allAges.map((age) => fundMap.get(age)?.fund || 0),
        label: `${label} - Fund Value`,
        color: colorSet.fund,
        // labelMarkType: "circle",
      },
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.income || 0),
        label: `${label} - Income`,
        color: colorSet.income,
        labelMarkType: "circle",
      },
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.tfc || 0),
        label: `${label} - TFC`,
        color: colorSet.tfc,
        labelMarkType: "circle",
      },
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.phased || 0),
        label: `${label} - Phased Tax-Free Cash`,
        color: colorSet.phased,

        labelMarkType: "circle",
      },
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.adhoc || 0),
        label: `${label} - Adhoc Withdrawals`,
        color: colorSet.adhoc,

        labelMarkType: "circle",
      },
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.adhocContribution || 0),
        label: `${label} - Adhoc Contributions`,
        color: colorSet.adhocContribution,
        labelMarkType: "circle",
      },
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.regularIncome || 0),
        label: `${label} - Contribution`,
        color: colorSet.regularIncome,
        labelMarkType: "circle",
      }
    ];
  });

  const chartSeries = series.length > 0 ? series : placeholderSeries;

  const visibleSeries = chartSeries.filter((s) =>
    s.data.some((v) => Number(v ?? 0) !== 0)
  );

  const graphHeight = useGraphHeight(isPrint);

  return (
    <div
      ref={chartOnlyRef}
      style={{
        width: isPrint ? 800 : "100%",
        height: isPrint ? 500 : "100%",
        backgroundColor: "transparent",
      }}
      id={idOverride || "graph"}
    >
      {/* <Button
        onClick={() => apiRef.current.exportAsImage()}
        variant="contained"
        sx={{ mb: 1 }}
      >
        Print
      </Button> */}
      {/* allAges.length > 0 ? */}
      {/* {allAges.length > 0 && personPensions ? ( */}
      <ChartDataProviderPro
        key={selectedPerson}
        series={visibleSeries}
        apiRef={apiRef}
        height={graphHeight}
        fillMode="none"
        // width={isPrint ? 2000 : 1200}
        xAxis={[
          {
            id: "age-axis",
            scaleType: "band",
            data: allAges.length ? allAges : [],
            label: "Age",
          },
        ]}
        yAxis={[
          {
            id: "value-axis",
            label: "Amount",
            valueFormatter: (val) => {
              if (val >= 1_000_000) return `£${(val / 1_000_000).toFixed(1)}m`;
              if (val >= 1_000) return `£${(val / 1_000).toFixed(0)}k`;
              return `£${val}`;
            },
            width: 70,
          },
        ]}
      >
        <CustomChartWrapper isApiPrint={apiRef ? true : false}>
          <ChartsLegend
            direction="horizontal"
            position="top"
            slotProps={{
              legend: {
                sx: {
                  fontSize: 12,
                  color: "#081e56",
                  fontFamily: "'Open Sans'",
                  fontWeight: 500,
                },
              },
            }}
          />
          <ChartsSurface>
            <defs>
              {series
                .filter((s) => s.type === "line")
                .map((s, idx) => (
                  <linearGradient
                    key={`grad-${idx}`}
                    id={`grad-${idx}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={hexToRgba(s.color, 0.2)} />
                    <stop offset="80%" stopColor={hexToRgba(s.color, 0)} />
                  </linearGradient>
                ))}
            </defs>

            <BarPlot
              // animated={animated.toString()}
              springconfig={{ tension: 80, friction: 80 }}
              style={{
                opacity: 0,
                animation: "fadeIn forwards ease-in-out",
              }}
            />

            {series
              .filter((s) => s.type === "line")
              .map((s, idx) => (
                <AreaPlot
                  key={`area-${idx}`}
                  seriesid={s.label}
                  // animated={animated.toString()}
                  springconfig={{ tension: 80, friction: 80 }}
                  style={{
                    opacity: 0,
                    animation: "fadeIn forwards ease-in-out",
                  }}
                  slots={{
                    area: ({ d, style }) => (
                      <path
                        d={d}
                        style={style}
                        fill={`url(#grad-${idx})`}
                        stroke="none"
                      />
                    ),
                  }}
                />
              ))}

            {series
              .filter((s) => s.type === "line")
              .map((s, idx) => (
                <LinePlot
                  key={`line-${idx}`}
                  seriesid={s.label}
                  slotProps={{
                    line: {
                      style: {
                        strokeWidth: 4,
                      },
                    },
                  }}
                  springconfig={{ tension: 80, friction: 80 }}
                  style={{
                    opacity: 0,
                    animation: "fadeIn forwards ease-in-out",
                  }}
                />
              ))}

            <ChartsXAxis
              axisId="age-axis"
              sx={{
                [`& .${axisClasses.tickLabel}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  fill: "#081e56",
                },
                [`& .${axisClasses.label}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  fill: "#081e56",
                },
              }}
            />
            <ChartsYAxis
              axisId="value-axis"
              sx={{
                [`& .${axisClasses.tickLabel}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  fill: "#081e56",
                },
                [`& .${axisClasses.label}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  fill: "#081e56",
                },
              }}
            />
            <ChartsTooltip />
          </ChartsSurface>
        </CustomChartWrapper>
      </ChartDataProviderPro>
      {/* ) : (
        <div />
      )} */}
    </div>
  );
};

export default PensionGraph;
